import React, { useState, useEffect, useCallback } from 'react'
import { CreditCard, Search, Gift, AlertTriangle, Check, ExternalLink, ChevronDown, ChevronUp, Infinity } from 'lucide-react'
import { getSubscriptions, getSubscriptionStats, grantPremium, updateSubscriptionStatus, getPaymentsByEmail } from '../services/subscriptionService.js'
import { useToast } from '../components/Toast.jsx'
import Pagination from '../components/Pagination.jsx'
import Modal from '../components/Modal.jsx'
import FormField from '../components/FormField.jsx'
import Breadcrumbs from '../components/Breadcrumbs.jsx'

const inputCls = 'w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'

const statusColors = {
  ACTIVE: 'bg-green-500/20 text-green-400',
  TRIALING: 'bg-blue-500/20 text-blue-400',
  CANCELED: 'bg-red-500/20 text-red-400',
  PAST_DUE: 'bg-amber-500/20 text-amber-400',
  INACTIVE: 'bg-gray-500/20 text-gray-400',
  PENDING_LAUNCH: 'bg-purple-500/20 text-purple-400',
}

const STATUS_OPTIONS = ['ACTIVE', 'TRIALING', 'CANCELED', 'PAST_DUE', 'INACTIVE', 'PENDING_LAUNCH']
// Pricing V3 + Premium restoration: PERSONAL_PRO and PERSONAL_PREMIUM are grantable. Free = no entitlement row.
// PERSONAL_BASIC is legacy: still displays in tables but cannot be selected here.
const PLAN_OPTIONS = ['PERSONAL_PRO', 'PERSONAL_PREMIUM']
const LEGACY_PLAN_KEYS = new Set(['PERSONAL_BASIC'])
const PLAN_VEHICLE_LIMITS = { PERSONAL_PRO: 5, PERSONAL_PREMIUM: 15 }
const planLabel = (p) => {
  if (!p) return ''
  const raw = p.replace('PERSONAL_', '')
  const short = raw.charAt(0) + raw.slice(1).toLowerCase()
  return LEGACY_PLAN_KEYS.has(p) ? `${short} (legacy)` : short
}
const planOptionLabel = (p) => {
  const limit = PLAN_VEHICLE_LIMITS[p]
  return limit ? `${planLabel(p)} (${limit} vehicles)` : planLabel(p)
}

const StatCard = ({ label, value, color = 'text-white' }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5">
    <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">{label}</p>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
  </div>
)

const SubscriptionsPage = () => {
  const toast = useToast()
  const [subs, setSubs] = useState([])
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [stats, setStats] = useState(null)

  // Grant modal
  const [grantOpen, setGrantOpen] = useState(false)
  const [grantForm, setGrantForm] = useState({ email: '', planKey: 'PERSONAL_PRO', durationType: 'days', days: 90, customDate: '' })
  const [granting, setGranting] = useState(false)

  // Expanded row for payments
  const [expandedEmail, setExpandedEmail] = useState(null)
  const [payments, setPayments] = useState([])
  const [paymentsLoading, setPaymentsLoading] = useState(false)

  const fetchSubs = useCallback(() => {
    setLoading(true)
    setError(null)
    getSubscriptions(page, 20, statusFilter, search)
      .then(res => {
        setSubs(res.data.content || [])
        setTotalPages(res.data.totalPages || 0)
        setTotalElements(res.data.totalElements || 0)
      })
      .catch(() => setError('Failed to load subscriptions'))
      .finally(() => setLoading(false))
  }, [page, statusFilter, search])

  useEffect(() => { fetchSubs() }, [fetchSubs])

  useEffect(() => {
    getSubscriptionStats()
      .then(res => setStats(res.data))
      .catch(() => {})
  }, [])

  const handleGrant = async () => {
    if (!grantForm.email) return
    setGranting(true)
    try {
      let validTo
      if (grantForm.durationType === 'lifetime') {
        validTo = '2099-12-31T23:59:59Z'
      } else if (grantForm.durationType === 'custom') {
        validTo = new Date(grantForm.customDate + 'T23:59:59Z').toISOString()
      } else {
        validTo = new Date(Date.now() + grantForm.days * 24 * 3600 * 1000).toISOString()
      }
      await grantPremium({ email: grantForm.email, planKey: grantForm.planKey, validTo })
      toast.success(`Granted ${grantForm.planKey} to ${grantForm.email}`)
      setGrantOpen(false)
      fetchSubs()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to grant subscription')
    } finally {
      setGranting(false)
    }
  }

  const handleStatusChange = async (entitlementUuid, newStatus) => {
    try {
      await updateSubscriptionStatus(entitlementUuid, newStatus)
      toast.success(`Status updated to ${newStatus}`)
      fetchSubs()
    } catch {
      toast.error('Failed to update status')
    }
  }

  const togglePayments = async (email) => {
    if (expandedEmail === email) {
      setExpandedEmail(null)
      return
    }
    setExpandedEmail(email)
    setPaymentsLoading(true)
    try {
      const res = await getPaymentsByEmail(email)
      setPayments(res.data || [])
    } catch {
      setPayments([])
    } finally {
      setPaymentsLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Subscriptions' }]} />

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Active" value={stats.active} color="text-green-400" />
          <StatCard label="Trialing" value={stats.trialing} color="text-blue-400" />
          <StatCard label="Canceled" value={stats.canceled} color="text-red-400" />
          <StatCard label="Monthly Revenue" value={`${stats.monthlyRevenue?.toFixed?.(2) || '0.00'} GBP`} color="text-emerald-400" />
          <StatCard label="Total Revenue" value={`${stats.totalRevenue?.toFixed?.(2) || '0.00'} GBP`} color="text-emerald-400" />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-[200px]">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Search email, Auth0 ID, Stripe ID..."
              value={search} onChange={e => { setSearch(e.target.value); setPage(0) }}
              className={`${inputCls} pl-10`} />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0) }}
            className={`${inputCls} max-w-[180px]`}>
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button onClick={() => { setGrantOpen(true); setGrantForm({ email: '', planKey: 'PERSONAL_PRO', durationType: 'days', days: 90, customDate: '' }) }}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all text-sm">
          <Gift className="w-4 h-4" /> Grant Premium
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
          <button onClick={fetchSubs} className="ml-auto underline hover:no-underline">Retry</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Email</th>
              <th className="text-center px-4 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Status</th>
              <th className="text-center px-4 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Plan</th>
              <th className="text-center px-4 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Billing</th>
              <th className="text-center px-4 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Type</th>
              <th className="text-left px-4 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Valid Until</th>
              <th className="text-left px-4 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Stripe</th>
              <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-12 text-gray-400">Loading...</td></tr>
            ) : subs.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-gray-400">No subscriptions found</td></tr>
            ) : subs.map(sub => (
              <React.Fragment key={sub.entitlementUuid}>
                <tr className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                  <td className="px-6 py-4">
                    <button onClick={() => togglePayments(sub.email)} className="flex items-center gap-2 text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors">
                      {expandedEmail === sub.email ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      {sub.email}
                    </button>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColors[sub.status] || 'bg-gray-500/20 text-gray-400'}`}>
                      {sub.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center text-xs font-mono">{planLabel(sub.planKey) || '-'}</td>
                  <td className="px-4 py-4 text-center text-xs">{sub.billing || '-'}</td>
                  <td className="px-4 py-4 text-center text-xs">{sub.purchaseType || '-'}</td>
                  <td className="px-4 py-4 text-sm text-gray-400">
                    {sub.validTo ? new Date(sub.validTo).toLocaleDateString() : '-'}
                    {sub.trialEndsAt && <span className="ml-1 text-xs text-blue-400">(trial: {new Date(sub.trialEndsAt).toLocaleDateString()})</span>}
                  </td>
                  <td className="px-4 py-4">
                    {sub.stripeCustomerId ? (
                      <a href={`https://dashboard.stripe.com/customers/${sub.stripeCustomerId}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300">
                        <ExternalLink className="w-3 h-3" /> Stripe
                      </a>
                    ) : <span className="text-xs text-gray-500">-</span>}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <select value={sub.status} onChange={e => handleStatusChange(sub.entitlementUuid, e.target.value)}
                      className="text-xs bg-transparent border border-gray-600 rounded-lg px-2 py-1 cursor-pointer">
                      {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
                {expandedEmail === sub.email && (
                  <tr key={`${sub.entitlementUuid}-payments`}>
                    <td colSpan={8} className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50">
                      <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Payment History</p>
                      {paymentsLoading ? (
                        <p className="text-sm text-gray-400">Loading payments...</p>
                      ) : payments.length === 0 ? (
                        <p className="text-sm text-gray-400">No payments recorded</p>
                      ) : (
                        <div className="space-y-2">
                          {payments.map(p => (
                            <div key={p.paymentUuid} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                              <div className="flex items-center gap-4">
                                <CreditCard className="w-4 h-4 text-gray-400" />
                                <div>
                                  <p className="text-sm font-medium">{p.amount} {p.currency}</p>
                                  <p className="text-xs text-gray-400">{p.description || `${p.planKey} ${p.billing}`}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${p.status === 'PAID' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                  {p.status}
                                </span>
                                <span className="text-xs text-gray-400">{p.occurredAt ? new Date(p.occurredAt).toLocaleDateString() : ''}</span>
                                {p.invoiceUrl && (
                                  <a href={p.invoiceUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300">
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} totalElements={totalElements} onPageChange={setPage} label="subscriptions" />

      {/* Grant Premium Modal */}
      <Modal open={grantOpen} title="Grant Premium Access" onClose={() => setGrantOpen(false)} maxWidth="max-w-md" footer={
        <>
          <button onClick={() => setGrantOpen(false)} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium text-sm">Cancel</button>
          <button onClick={handleGrant} disabled={!grantForm.email || granting || (grantForm.durationType === 'custom' && !grantForm.customDate)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 text-sm">
            <Gift className="w-4 h-4" /> {granting ? 'Granting...' : 'Grant Access'}
          </button>
        </>
      }>
        <FormField label="User Email" htmlFor="grant-email">
          <input id="grant-email" type="email" placeholder="user@example.com"
            value={grantForm.email} onChange={e => setGrantForm(f => ({ ...f, email: e.target.value }))}
            className={inputCls} />
        </FormField>
        <FormField label="Plan" htmlFor="grant-plan">
          <select id="grant-plan" value={grantForm.planKey} onChange={e => setGrantForm(f => ({ ...f, planKey: e.target.value }))}
            className={inputCls}>
            {PLAN_OPTIONS.map(p => <option key={p} value={p}>{planOptionLabel(p)}</option>)}
          </select>
        </FormField>
        <FormField label="Duration" htmlFor="grant-duration">
          <div className="flex gap-2 mb-2">
            {[
              { key: 'days', label: 'Days' },
              { key: 'custom', label: 'Custom Date' },
              { key: 'lifetime', label: 'Lifetime', icon: Infinity },
            ].map(opt => (
              <button key={opt.key} type="button"
                onClick={() => setGrantForm(f => ({ ...f, durationType: opt.key }))}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  grantForm.durationType === opt.key
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400 border border-transparent hover:border-gray-500'
                }`}>
                {opt.icon && <opt.icon className="w-3 h-3" />}
                {opt.label}
              </button>
            ))}
          </div>
          {grantForm.durationType === 'days' && (
            <input id="grant-duration" type="number" min="1" max="3650"
              value={grantForm.days} onChange={e => setGrantForm(f => ({ ...f, days: Number(e.target.value) }))}
              className={inputCls} placeholder="Number of days" />
          )}
          {grantForm.durationType === 'custom' && (
            <input id="grant-duration" type="date"
              value={grantForm.customDate} onChange={e => setGrantForm(f => ({ ...f, customDate: e.target.value }))}
              className={inputCls} min={new Date().toISOString().split('T')[0]} />
          )}
          {grantForm.durationType === 'lifetime' && (
            <p className="text-xs text-amber-400 flex items-center gap-1.5 mt-1">
              <Infinity className="w-4 h-4" /> Subscription valid until 2099-12-31
            </p>
          )}
        </FormField>
      </Modal>
    </div>
  )
}

export default SubscriptionsPage
